import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../api/axios';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError('');

        try {
            const response = await api.post('/auth/forgot-password', { email });

            await response.data;

            if (response.status === 200) {
                setSuccess(true);
            } else {
                setError('Failed to send reset email');
            }
        } catch (err) {
            console.error(err);
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: "linear-gradient(135deg, #4e73df 0%, #1cc88a 100%)" }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <div className="card shadow-lg border-0 rounded-4">
                                <div className="card-body p-5 text-center">
                                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 bg-success"
                                        style={{ width: "60px", height: "60px" }}>
                                        <CheckCircle size={32} className="text-white" />
                                    </div>
                                    <h2 className="fw-bold text-dark mb-3">Check Your Email</h2>
                                    <p className="text-muted mb-4">
                                        We've sent a password reset link to <strong>{email}</strong>
                                    </p>
                                    <p className="text-muted mb-4">
                                        The link will expire in 15 minutes. If you don't see the email,
                                        check your spam folder.
                                    </p>
                                    <Link to="/login" className="btn btn-primary btn-lg rounded-pill">
                                        <ArrowLeft size={20} className="me-2" />
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center"
            style={{ background: "linear-gradient(135deg, #4e73df 0%, #1cc88a 100%)" }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <div className="card shadow-lg border-0 rounded-4">
                            <div className="card-body p-5">
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 bg-light"
                                        style={{ width: "60px", height: "60px" }}>
                                        <Mail size={32} className="text-primary" />
                                    </div>
                                    <h2 className="fw-bold text-dark">Forgot Password?</h2>
                                    <p className="text-muted mb-0">
                                        Enter your email address and we'll send you a link to reset your password.
                                    </p>
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="alert alert-danger mb-4" role="alert">
                                        {error}
                                    </div>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <label htmlFor="email" className="form-label fw-semibold">
                                            Email address
                                        </label>
                                        <div className="input-group input-group-lg">
                                            <span className="input-group-text bg-light">
                                                <Mail size={20} className="text-muted" />
                                            </span>
                                            <input
                                                type="email"
                                                id="email"
                                                className="form-control"
                                                placeholder="Enter your email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled={loading}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg w-100 mb-3 rounded-pill"
                                        disabled={loading || !email}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Sending...
                                            </>
                                        ) : (
                                            "Send Reset Link"
                                        )}
                                    </button>

                                    <div className="text-center">
                                        <Link to="/login" className="text-decoration-none">
                                            <ArrowLeft size={16} className="me-1" />
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
